import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiLock, FiEye, FiEyeOff, FiCheckCircle } from 'react-icons/fi';
import api from '../../utils/api';
import './AuthPages.css';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      setError('הסיסמה חייבת להכיל לפחות 6 תווים');
      return;
    }

    if (password !== confirmPassword) {
      setError('הסיסמאות אינן תואמות');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      await api.post('/auth/reset-password', { token, password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה באיפוס הסיסמה');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <motion.div
            className="auth-form-section"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="auth-form-content">
              <Link to="/" className="logo">
                <span className="logo-ai">AI</span>
                <span className="logo-weon">weOn</span>
              </Link>
              <h1>קישור לא תקין</h1>
              <p className="auth-subtitle">הקישור לאיפוס הסיסמה אינו תקין או שפג תוקפו.</p>
              <Link to="/forgot-password" className="btn btn-primary btn-lg">
                בקש קישור חדש
              </Link>
            </div>
          </motion.div>

          <motion.div
            className="auth-visual-section"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="visual-content">
              <h2>איפוס סיסמה</h2>
              <h2 className="text-primary">בקלות ובמהירות</h2>
            </div>
            <div className="visual-shapes">
              <div className="shape shape-1"></div>
              <div className="shape shape-2"></div>
              <div className="shape shape-3"></div>
              <div className="shape shape-4"></div>
              <div className="shape shape-5"></div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* Left Side - Form */}
        <motion.div
          className="auth-form-section"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <div className="auth-form-content">
            <Link to="/" className="logo">
              <span className="logo-ai">AI</span>
              <span className="logo-weon">weOn</span>
            </Link>

            {success ? (
              <div className="auth-confirmation">
                <div className="success-icon-large">
                  <FiCheckCircle />
                </div>
                <h1>הסיסמה עודכנה</h1>
                <p className="auth-subtitle">הסיסמה עודכנה בהצלחה! מועבר לדף ההתחברות...</p>
              </div>
            ) : (
              <>
                <h1>איפוס סיסמה</h1>
                <p className="auth-subtitle">בחר סיסמה חדשה לחשבון שלך</p>

                <form onSubmit={handleSubmit} className="auth-form">
                  <div className="form-group">
                    <label className="form-label">סיסמה חדשה</label>
                    <div className="input-with-icon">
                      <FiLock className="input-icon" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="6 תווים לפחות"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={6}
                        className="form-input"
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">אישור סיסמה</label>
                    <div className="input-with-icon">
                      <FiLock className="input-icon" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="הזן שוב את הסיסמה"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        minLength={6}
                        className="form-input"
                      />
                    </div>
                  </div>

                  {error && <span className="form-error">{error}</span>}

                  <button
                    type="submit"
                    className="btn btn-primary btn-lg"
                    disabled={isLoading}
                  >
                    {isLoading ? 'מעדכן...' : 'עדכן סיסמה'}
                  </button>
                </form>
              </>
            )}
          </div>
        </motion.div>

        {/* Right Side - Visual */}
        <motion.div
          className="auth-visual-section"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <div className="visual-content">
            <h2>איפוס סיסמה</h2>
            <h2 className="text-primary">בקלות ובמהירות</h2>
          </div>
          <div className="visual-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
            <div className="shape shape-4"></div>
            <div className="shape shape-5"></div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
